// Yerel LTX-2.3 ile AI video uretimi - kompakt panel.
//
// Video Studio sayfasinin tam surumunun yaninda, Mockup Studio gibi zaten
// bir gorseli olan yerlere gomulmek icin. Urun tipini magaza dikeyi ve
// urun adindan kendi secer; kullanici yine de degistirebilir.

import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, Sparkles, AlertCircle } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useShopVertical } from "../../lib/useShopVertical";
import { detectPreset } from "../../lib/ltxDetect";
import bundledTemplate from "../../lib/ltx-i2v.api.json";
import {
  buildPrompt,
  checkComfy,
  queuePrompt,
  uploadImage,
  watchJob,
  type ApiWorkflow,
  type Progress,
  type VideoOutput,
} from "../../lib/comfyui";
import {
  ASPECTS,
  DURATIONS,
  PRODUCT_PRESETS,
  dimensionsFor,
  framesFor,
  negativeFor,
} from "../../lib/ltxPresets";

const TEMPLATE = bundledTemplate as unknown as ApiWorkflow;
const FPS = 24;
const LONG_EDGE = 512; // kompakt panelde hiz onemli; kalite secimi tam sayfada

/** data:, blob: ve uzak URL'lerin hepsini File'a cevirir. */
async function toFile(url: string): Promise<File> {
  // Uzak http(s) adresleri CORS'a takilir; API proxy'sinden gecir.
  const needsProxy = /^https?:\/\//i.test(url) && !url.startsWith(getBackendUrl());
  const target = needsProxy
    ? `${getBackendUrl()}/api/media/proxy-image?url=${encodeURIComponent(url)}`
    : url;
  const res = await fetch(target, needsProxy ? { headers: authHeaders() } : undefined);
  if (!res.ok) throw new Error(`Görsel alınamadı (${res.status})`);
  const blob = await res.blob();
  const ext = (blob.type.split("/")[1] ?? "jpg").replace("jpeg", "jpg");
  return new File([blob], `erc-${Date.now()}.${ext}`, { type: blob.type });
}

export function LtxVideoPanel({
  imageUrl,
  title,
  aspectDefault = "1-1",
}: {
  imageUrl: string | null;
  title?: string | null;
  aspectDefault?: string;
}) {
  const { vertical } = useShopVertical();

  const [presetId, setPresetId] = useState(PRODUCT_PRESETS[0].id);
  const [detectReason, setDetectReason] = useState<string | null>(null);
  const [aspectId, setAspectId] = useState(aspectDefault);
  const [seconds, setSeconds] = useState<number>(PRODUCT_PRESETS[0].seconds);
  const [scene, setScene] = useState("");

  const [online, setOnline] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [result, setResult] = useState<VideoOutput | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preset = useMemo(
    () => PRODUCT_PRESETS.find((p) => p.id === presetId) ?? PRODUCT_PRESETS[0],
    [presetId],
  );
  const aspect = useMemo(() => ASPECTS.find((a) => a.id === aspectId) ?? ASPECTS[0], [aspectId]);

  useEffect(() => {
    checkComfy()
      .then(() => setOnline(true))
      .catch(() => setOnline(false));
  }, []);

  // Urun adi ya da magaza degisince tipi yeniden algila
  useEffect(() => {
    const det = detectPreset({ vertical, title });
    setPresetId(det.presetId);
    setDetectReason(det.reason);
    const p = PRODUCT_PRESETS.find((x) => x.id === det.presetId);
    if (p) setSeconds(p.seconds);
  }, [vertical, title]);

  const generate = async () => {
    if (!imageUrl) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress({ ratio: null, label: "Görsel hazırlanıyor" });
    try {
      const file = await toFile(imageUrl);
      const img = await uploadImage(file);
      const dims = dimensionsFor(aspect, LONG_EDGE);
      const wf = buildPrompt(TEMPLATE, {
        imageName: img.name,
        prompt: `${scene.trim()} ${preset.promptSuffix}`.trim(),
        negativePrompt: negativeFor(preset),
        width: dims.width,
        height: dims.height,
        frames: framesFor(seconds, FPS),
        strength: preset.strength,
        fps: FPS,
        withAudio: false,
      });
      const job = await queuePrompt(wf);
      setProgress({ ratio: null, label: "Model yükleniyor" });
      const outs = await watchJob(job, setProgress);
      if (!outs.length) throw new Error("Video çıktısı bulunamadı");
      setResult(outs[outs.length - 1]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  if (online === false) {
    return (
      <div className="text-[10px] text-gray-400 bg-gray-50 rounded p-2 flex items-start gap-1.5">
        <AlertCircle className="h-3 w-3 shrink-0 mt-px" />
        <span>
          AI video için yerel ComfyUI çalışmalı (<code>run_ltx_panel.bat</code>). Slideshow videosu
          bundan bağımsız çalışır.
        </span>
      </div>
    );
  }

  const pill = (active: boolean) =>
    `text-[10px] px-2 py-1 rounded border ${
      active ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium" : "border-gray-200 text-gray-500 hover:bg-gray-50"
    }`;

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-indigo-500" />
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
          AI Video (yerel LTX)
        </span>
      </div>

      <div>
        <span className="text-[10px] text-gray-600 font-medium block mb-1">
          Ürün tipi
          {detectReason && <span className="text-gray-400 font-normal"> · {detectReason}</span>}
        </span>
        <div className="flex flex-wrap gap-1">
          {PRODUCT_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setPresetId(p.id);
                setSeconds(p.seconds);
                setDetectReason("elle seçildi");
              }}
              className={pill(presetId === p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <span className="text-[10px] text-gray-600 font-medium block mb-1">Format</span>
          <div className="flex flex-wrap gap-1">
            {ASPECTS.slice(0, 4).map((a) => (
              <button key={a.id} onClick={() => setAspectId(a.id)} className={pill(aspectId === a.id)}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <span className="text-[10px] text-gray-600 font-medium block mb-1">Süre</span>
          <div className="flex flex-wrap gap-1">
            {DURATIONS.map((d) => (
              <button key={d} onClick={() => setSeconds(d)} className={pill(seconds === d)}>
                {d}s
              </button>
            ))}
          </div>
        </div>
      </div>

      <input
        value={scene}
        onChange={(e) => setScene(e.target.value)}
        placeholder="Sahne (opsiyonel): yumuşak doğal ışık"
        className="w-full border rounded px-2 py-1.5 text-[10px]"
      />

      <button
        onClick={() => void generate()}
        disabled={!imageUrl || running}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {running ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {progress?.label ?? "Üretiliyor"}
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            AI Video Üret
          </>
        )}
      </button>

      {!imageUrl && <p className="text-[10px] text-amber-500">Once bir urun gorseli ekleyin.</p>}
      {error && <div className="text-[10px] text-red-500 bg-red-50 rounded p-2">{error}</div>}

      {result && (
        <div className="space-y-1.5">
          <video src={result.url} controls loop muted className="w-full rounded-lg border" />
          <a
            href={result.url}
            download={result.filename}
            className="flex items-center justify-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg"
          >
            <Download className="h-3 w-3" />
            İndir
          </a>
        </div>
      )}
    </div>
  );
}
